import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  CircularProgress,
  MenuItem,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SyncIcon from "@mui/icons-material/Sync";
import { useTranslation } from "react-i18next";
import {
  useGetCompanyBillingsQuery,
  useCreateCompanyBillingMutation,
  useUpdateCompanyBillingMutation,
  useDeleteCompanyBillingMutation,
  useSyncBillingFromHoldedMutation,
} from "../../store/apis/companyApi";
import { useDispatch } from "react-redux";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { CompanyBilling } from "../../types/CompanyTypes";
import { LEGAL_NOTE_OPTIONS } from "../../utils/billing/legalNotes";
import HoldedContactPicker from "./HoldedContactPicker";
import { HoldedContactPreview } from "../../store/apis/companyApi";
import * as Sentry from "@sentry/react";

interface BillingManagementProps {
  companyId: string;
}

interface BillingFormData {
  nameFiscal: string;
  nif: string;
  contactName: string;
  email: string;
  phoneNumber: string;
  address: string;
  city: string;
  postalCode: string;
  province: string;
  country: string;
  holdedContactId: string;
  legalNote: string;
  isDefault: boolean;
}

const emptyBillingForm: BillingFormData = {
  nameFiscal: "",
  nif: "",
  contactName: "",
  email: "",
  phoneNumber: "",
  address: "",
  city: "",
  postalCode: "",
  province: "",
  country: "",
  holdedContactId: "",
  legalNote: "",
  isDefault: false,
};

const BillingManagement: React.FC<BillingManagementProps> = ({ companyId }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Get existing billing data
  const { data: billingData, isLoading: isLoadingBillings } =
    useGetCompanyBillingsQuery(companyId);

  // Billing mutations
  const [createBilling, { isLoading: isCreating }] =
    useCreateCompanyBillingMutation();
  const [updateBilling, { isLoading: isUpdating }] =
    useUpdateCompanyBillingMutation();
  const [deleteBilling, { isLoading: isDeleting }] =
    useDeleteCompanyBillingMutation();
  const [syncBilling, { isLoading: isSyncing }] =
    useSyncBillingFromHoldedMutation();

  // Track which billing is currently syncing (for per-row spinner).
  const [syncingId, setSyncingId] = useState<string | null>(null);

  // Inline draft of a Holded contact ID being pasted for a billing that
  // doesn't have one yet, so it can be linked + synced in one click without
  // opening the full edit form. Keyed by billing id.
  const [quickHoldedIdDrafts, setQuickHoldedIdDrafts] = useState<
    Record<string, string>
  >({});
  const [linkingId, setLinkingId] = useState<string | null>(null);

  // State for billing entries
  const [billingEntries, setBillingEntries] = useState<
    (CompanyBilling & { isEditing?: boolean })[]
  >([]);
  const [newBillingEntry, setNewBillingEntry] =
    useState<BillingFormData>(emptyBillingForm);
  const [showNewBillingForm, setShowNewBillingForm] = useState(false);

  // Load billing data when available
  useEffect(() => {
    if (billingData?.data) {
      const billings = Array.isArray(billingData.data)
        ? billingData.data
        : [billingData.data];

      // Sort billings to show default first
      const sortedBillings = [...billings].sort((a, b) => {
        if (a.isDefault) return -1;
        if (b.isDefault) return 1;
        return 0;
      });

      setBillingEntries(
        sortedBillings.map((billing) => ({ ...billing, isEditing: false }))
      );
    }
  }, [billingData]);

  // Handle input change for new billing entry
  const handleNewBillingInputChange =
    (field: keyof BillingFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setNewBillingEntry({
        ...newBillingEntry,
        [field]:
          field === "isDefault" ? event.target.checked : event.target.value,
      });
    };

  // Handle input change for existing billing entries
  const handleBillingInputChange =
    (index: number, field: keyof BillingFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const updatedEntries = [...billingEntries];
      updatedEntries[index] = {
        ...updatedEntries[index],
        [field]:
          field === "isDefault" ? event.target.checked : event.target.value,
      };
      setBillingEntries(updatedEntries);
    };

  // Campos fiscales que trae Holded. Se sobrescriben tal cual porque Holded es la
  // fuente de verdad; la nota legal y el "por defecto" son decisiones nuestras y
  // no se tocan.
  const holdedFiscalFields = (preview: HoldedContactPreview) => ({
    holdedContactId: preview.holdedContactId,
    nameFiscal: preview.nameFiscal,
    nif: preview.nif,
    email: preview.email,
    phoneNumber: preview.phoneNumber,
    address: preview.address,
    city: preview.city,
    postalCode: preview.postalCode,
    province: preview.province,
    country: preview.country,
    contactName: preview.contactName,
  });

  const fillNewBillingFromHolded = (preview: HoldedContactPreview) => {
    setNewBillingEntry((previous) => ({
      ...previous,
      ...holdedFiscalFields(preview),
    }));
  };

  // Rellena la fila en edición sin guardar: el usuario revisa y pulsa Guardar.
  const fillBillingFromHolded = (
    index: number,
    preview: HoldedContactPreview
  ) => {
    setBillingEntries((previous) =>
      previous.map((entry, i) =>
        i === index ? { ...entry, ...holdedFiscalFields(preview) } : entry
      )
    );
  };

  // El backend rechaza null en los campos opcionales de billing (422). Manda
  // siempre string vacío en lugar de null/undefined (legalNote, holdedContactId…).
  const sanitizeBillingPayload = <T extends object>(payload: T): T =>
    Object.fromEntries(
      Object.entries(payload).map(([key, value]) => [
        key,
        value == null ? "" : value,
      ])
    ) as unknown as T;

  // Create a new billing entry
  const handleCreateBilling = async () => {
    if (
      !newBillingEntry.contactName ||
      !newBillingEntry.email ||
      !newBillingEntry.phoneNumber
    ) {
      dispatch(
        setToastMessage({
          message: t("billing.errors.requiredFields"),
          severity: "error",
        })
      );
      return;
    }
    try {
      await createBilling({
        companyId,
        billing: sanitizeBillingPayload(newBillingEntry),
      }).unwrap();

      setNewBillingEntry(emptyBillingForm);
      setShowNewBillingForm(false);
      dispatch(
        setToastMessage({
          message: t("billing.createSuccess"),
          severity: "success",
        })
      );
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error creating billing:", error);
      dispatch(
        setToastMessage({
          message: t("billing.createError"),
          severity: "error",
        })
      );
    }
  };

  // Update an existing billing entry
  const handleUpdateBilling = async (billingId: string, index: number) => {
    try {
      const billingToUpdate = billingEntries[index];
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, companyId, createdAt, updatedAt, isEditing, ...updates } =
        billingToUpdate;

      await updateBilling({
        billingId,
        updates: sanitizeBillingPayload(updates),
      }).unwrap();

      // Update local state to exit editing mode
      const updatedEntries = [...billingEntries];
      updatedEntries[index] = { ...updatedEntries[index], isEditing: false };
      setBillingEntries(updatedEntries);

      dispatch(
        setToastMessage({
          message: t("billing.updateSuccess"),
          severity: "success",
        })
      );
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error updating billing:", error);
      dispatch(
        setToastMessage({
          message: t("billing.updateError"),
          severity: "error",
        })
      );
    }
  };

  // Delete a billing entry
  const handleDeleteBilling = async (billingId: string) => {
    try {
      await deleteBilling(billingId).unwrap();
      dispatch(
        setToastMessage({
          message: t("billing.deleteSuccess"),
          severity: "success",
        })
      );
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error deleting billing:", error);
      dispatch(
        setToastMessage({
          message: t("billing.deleteError"),
          severity: "error",
        })
      );
    }
  };

  // Toggle editing mode for a billing entry
  const toggleEditMode = (index: number) => {
    const updatedEntries = [...billingEntries];
    updatedEntries[index] = {
      ...updatedEntries[index],
      isEditing: !updatedEntries[index].isEditing,
    };
    setBillingEntries(updatedEntries);
  };

  // Set a billing entry as default
  const handleSetDefault = async (billingId: string, index: number) => {
    try {
      // First, set all entries to non-default
      const updatedEntries = billingEntries.map((entry) => ({
        ...entry,
        isDefault: false,
      }));

      // Then set the selected one as default
      updatedEntries[index] = { ...updatedEntries[index], isDefault: true };
      setBillingEntries(updatedEntries);

      // Update the billing entry
      await updateBilling({
        billingId,
        updates: { isDefault: true },
      }).unwrap();

      dispatch(
        setToastMessage({
          message: t("billing.defaultUpdated"),
          severity: "success",
        })
      );
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error setting default billing:", error);
      dispatch(
        setToastMessage({
          message: t("billing.updateError"),
          severity: "error",
        })
      );
    }
  };

  // Sync a single billing's fiscal data from Holded.
  const handleSyncBilling = async (billingId: string) => {
    setSyncingId(billingId);
    try {
      await syncBilling(billingId).unwrap();
      // La invalidación de CompanyBillings refresca los datos en pantalla.
      dispatch(
        setToastMessage({
          message: t("billing.syncSuccess"),
          severity: "success",
        })
      );
    } catch (error) {
      Sentry.captureException(error);
      const message =
        (error as { data?: { message?: string } })?.data?.message ||
        t("billing.syncError");
      dispatch(setToastMessage({ message, severity: "error" }));
    } finally {
      setSyncingId(null);
    }
  };

  // Quick path for a billing that has no Holded contact ID yet: save the
  // pasted ID and immediately sync, without opening the full edit form.
  const handleQuickLinkAndSync = async (billingId: string) => {
    const holdedContactId = (quickHoldedIdDrafts[billingId] || "").trim();
    if (!holdedContactId) return;

    setLinkingId(billingId);
    try {
      await updateBilling({
        billingId,
        updates: { holdedContactId },
      }).unwrap();
      await syncBilling(billingId).unwrap();

      setQuickHoldedIdDrafts((prev) => {
        const next = { ...prev };
        delete next[billingId];
        return next;
      });

      dispatch(
        setToastMessage({
          message: t("billing.linkSuccess"),
          severity: "success",
        })
      );
    } catch (error) {
      Sentry.captureException(error);
      const message =
        (error as { data?: { message?: string } })?.data?.message ||
        t("billing.linkError");
      dispatch(setToastMessage({ message, severity: "error" }));
    } finally {
      setLinkingId(null);
    }
  };

  if (isLoadingBillings) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 4 }}
    >
      {/* Header with Add Button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: 48,
          minHeight: 48,
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          {t("billing.manageBillings")}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowNewBillingForm(true)}
          sx={{
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            borderRadius: "50px",
            px: 3,
            py: 1.5,
            boxShadow: "0px 8px 8px rgba(0, 0, 0, 0.04)",
            "&:hover": {
              bgcolor: theme.palette.primary.dark,
            },
          }}
        >
          {t("billing.addNew")}
        </Button>
      </Box>

      {/* New Billing Form */}
      {showNewBillingForm && (
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.04)",
            border: `1px solid ${theme.palette.grey[200]}`,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              color={theme.palette.primary.main}
            >
              {t("billing.newBilling")}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={newBillingEntry.isDefault}
                  onChange={handleNewBillingInputChange("isDefault")}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" fontSize={12}>
                  {t("billing.setAsDefault")}
                </Typography>
              }
            />
          </Box>

          <HoldedContactPicker
            holdedContactId={newBillingEntry.holdedContactId}
            onChangeContactId={(holdedContactId) =>
              setNewBillingEntry((previous) => ({
                ...previous,
                holdedContactId,
              }))
            }
            onFill={fillNewBillingFromHolded}
          />

          {/* Fiscal Information */}
          <Box sx={{ display: "flex", gap: 4 }}>
            <Box
              sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
            >
              <Typography variant="body2" fontWeight="medium">
                {t("billing.fiscalName", "Fiscal Name")}
              </Typography>
              <TextField
                value={newBillingEntry.nameFiscal}
                onChange={handleNewBillingInputChange("nameFiscal")}
                fullWidth
                variant="outlined"
                placeholder={t(
                  "billing.fiscalNamePlaceholder",
                  "e.g. My Company SL"
                )}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                  },
                }}
              />
            </Box>
            <Box
              sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
            >
              <Typography variant="body2" fontWeight="medium">
                {t("billing.nifVat", "NIF / VAT Number")}
              </Typography>
              <TextField
                value={newBillingEntry.nif}
                onChange={handleNewBillingInputChange("nif")}
                fullWidth
                variant="outlined"
                placeholder={t("billing.nifVatPlaceholder", "e.g. B12345678")}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                  },
                }}
              />
            </Box>
          </Box>

          {/* Contact Information */}
          <Box sx={{ display: "flex", gap: 4 }}>
            <Box
              sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
            >
              <Typography variant="body2" fontWeight="medium">
                {t("billing.contactName")}
              </Typography>
              <TextField
                value={newBillingEntry.contactName}
                onChange={handleNewBillingInputChange("contactName")}
                fullWidth
                variant="outlined"
                placeholder={t("billing.contactNamePlaceholder")}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                  },
                }}
              />
            </Box>
            <Box
              sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
            >
              <Typography variant="body2" fontWeight="medium">
                {t("billing.email")}
              </Typography>
              <TextField
                value={newBillingEntry.email}
                onChange={handleNewBillingInputChange("email")}
                fullWidth
                variant="outlined"
                placeholder={t("billing.emailPlaceholder")}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                  },
                }}
              />
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 4 }}>
            <Box
              sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
            >
              <Typography variant="body2" fontWeight="medium">
                {t("billing.phoneNumber")}
              </Typography>
              <TextField
                value={newBillingEntry.phoneNumber}
                onChange={handleNewBillingInputChange("phoneNumber")}
                fullWidth
                variant="outlined"
                placeholder={t("billing.phonePlaceholder")}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                  },
                }}
              />
            </Box>
            <Box
              sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
            >
              <Typography variant="body2" fontWeight="medium">
                {t("billing.address")}
              </Typography>
              <TextField
                value={newBillingEntry.address}
                onChange={handleNewBillingInputChange("address")}
                fullWidth
                variant="outlined"
                placeholder={t("billing.addressPlaceholder")}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                  },
                }}
              />
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 4 }}>
            <Box
              sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
            >
              <Typography variant="body2" fontWeight="medium">
                {t("billing.city")}
              </Typography>
              <TextField
                value={newBillingEntry.city}
                onChange={handleNewBillingInputChange("city")}
                fullWidth
                variant="outlined"
                placeholder={t("billing.cityPlaceholder")}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                  },
                }}
              />
            </Box>
            <Box
              sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
            >
              <Typography variant="body2" fontWeight="medium">
                {t("billing.postalCode")}
              </Typography>
              <TextField
                value={newBillingEntry.postalCode}
                onChange={handleNewBillingInputChange("postalCode")}
                fullWidth
                variant="outlined"
                placeholder={t("billing.postalCodePlaceholder")}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                  },
                }}
              />
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 4 }}>
            <Box
              sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
            >
              <Typography variant="body2" fontWeight="medium">
                {t("billing.province")}
              </Typography>
              <TextField
                value={newBillingEntry.province}
                onChange={handleNewBillingInputChange("province")}
                fullWidth
                variant="outlined"
                placeholder={t("billing.provincePlaceholder")}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                  },
                }}
              />
            </Box>
            <Box
              sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
            >
              <Typography variant="body2" fontWeight="medium">
                {t("billing.country")}
              </Typography>
              <TextField
                value={newBillingEntry.country}
                onChange={handleNewBillingInputChange("country")}
                fullWidth
                variant="outlined"
                placeholder={t("billing.countryPlaceholder")}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                  },
                }}
              />
            </Box>
          </Box>

          {/* Holded */}
          <Box sx={{ display: "flex", gap: 4 }}>
            <Box
              sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
            >
              <Typography variant="body2" fontWeight="medium">
                {t("billing.legalNote")}
              </Typography>
              <TextField
                select
                value={newBillingEntry.legalNote}
                onChange={handleNewBillingInputChange("legalNote")}
                fullWidth
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                  },
                }}
              >
                {LEGAL_NOTE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.labelKey} value={opt.value}>
                    {t(opt.labelKey)}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>

          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                setShowNewBillingForm(false);
                setNewBillingEntry(emptyBillingForm);
              }}
              sx={{
                borderRadius: "50px",
                px: 3,
              }}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateBilling}
              disabled={isCreating}
              sx={{
                bgcolor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                borderRadius: "50px",
                px: 3,
                "&:hover": {
                  bgcolor: theme.palette.primary.dark,
                },
              }}
            >
              {isCreating ? <CircularProgress size={24} /> : t("common.save")}
            </Button>
          </Box>
        </Box>
      )}

      {/* Existing Billing Entries */}
      {billingEntries.map((billing, index) => (
        <Box
          key={billing.id}
          sx={{
            p: 2,
            borderRadius: 2,
            boxShadow: billing.isDefault
              ? "0px 4px 12px rgba(0, 0, 0, 0.08)"
              : "0px 4px 8px rgba(0, 0, 0, 0.04)",
            border: billing.isDefault
              ? `2px solid ${theme.palette.primary.main}`
              : `1px solid ${theme.palette.grey[200]}`,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            bgcolor: billing.isDefault
              ? `${theme.palette.primary.light}10`
              : "white",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              color={theme.palette.primary.main}
            >
              {t("billing.billingEntry")} {index + 1}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {billing.isDefault && (
                  <Typography
                    variant="body2"
                    sx={{
                      mr: 1,
                      fontWeight: "bold",
                      color: theme.palette.primary.main,
                      bgcolor: `${theme.palette.primary.light}30`,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 10,
                      fontSize: "0.75rem",
                    }}
                  >
                    {t("billing.defaultBilling")}
                  </Typography>
                )}
                <FormControlLabel
                  control={
                    <Switch
                      checked={billing.isDefault}
                      onChange={() =>
                        !billing.isDefault &&
                        handleSetDefault(billing.id, index)
                      }
                      color="primary"
                      disabled={billing.isDefault || billing.isEditing}
                    />
                  }
                  label={
                    !billing.isDefault ? (
                      <Typography variant="body2" fontSize={12}>
                        {t("billing.setAsDefault")}
                      </Typography>
                    ) : null
                  }
                />
              </Box>
              {!billing.isEditing ? (
                <>
                  {billing.holdedContactId ? (
                    <Button
                      variant="outlined"
                      startIcon={
                        isSyncing && syncingId === billing.id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <SyncIcon />
                        )
                      }
                      onClick={() => handleSyncBilling(billing.id)}
                      disabled={isSyncing && syncingId === billing.id}
                      sx={{ borderRadius: "50px", minWidth: "auto" }}
                    >
                      {t("billing.syncFromHolded")}
                    </Button>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <TextField
                        size="small"
                        placeholder={t("billing.quickLinkPlaceholder")}
                        value={quickHoldedIdDrafts[billing.id] || ""}
                        onChange={(e) =>
                          setQuickHoldedIdDrafts((prev) => ({
                            ...prev,
                            [billing.id]: e.target.value,
                          }))
                        }
                        sx={{
                          width: 220,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "50px",
                          },
                        }}
                      />
                      <Button
                        variant="outlined"
                        startIcon={
                          linkingId === billing.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <SyncIcon />
                          )
                        }
                        onClick={() => handleQuickLinkAndSync(billing.id)}
                        disabled={
                          !quickHoldedIdDrafts[billing.id]?.trim() ||
                          linkingId === billing.id
                        }
                        sx={{ borderRadius: "50px", minWidth: "auto" }}
                      >
                        {t("billing.linkAndSync")}
                      </Button>
                    </Box>
                  )}
                  <Button
                    variant="outlined"
                    onClick={() => toggleEditMode(index)}
                    sx={{
                      borderRadius: "50px",
                      minWidth: "auto",
                    }}
                  >
                    {t("common.edit")}
                  </Button>
                </>
              ) : (
                <Button
                  variant="contained"
                  onClick={() => handleUpdateBilling(billing.id, index)}
                  disabled={isUpdating}
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    borderRadius: "50px",
                    minWidth: "auto",
                    "&:hover": {
                      bgcolor: theme.palette.primary.dark,
                    },
                  }}
                >
                  {isUpdating ? (
                    <CircularProgress size={24} />
                  ) : (
                    t("common.save")
                  )}
                </Button>
              )}
              <Button
                variant="outlined"
                color="error"
                onClick={() => handleDeleteBilling(billing.id)}
                disabled={isDeleting || billing.isDefault}
                sx={{
                  borderRadius: "50px",
                  minWidth: "auto",
                }}
              >
                <DeleteIcon />
              </Button>
            </Box>
          </Box>

          {billing.isEditing && (
            <HoldedContactPicker
              holdedContactId={billing.holdedContactId || ""}
              onChangeContactId={(holdedContactId) =>
                setBillingEntries((previous) =>
                  previous.map((entry, i) =>
                    i === index ? { ...entry, holdedContactId } : entry
                  )
                )
              }
              onFill={(preview) => fillBillingFromHolded(index, preview)}
            />
          )}

          {/* Fiscal Information */}
          <Box sx={{ display: "flex", gap: 4 }}>
            <Box
              sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
            >
              <Typography variant="body2" fontWeight="medium">
                {t("billing.fiscalName", "Fiscal Name")}
              </Typography>
              <TextField
                value={billing.nameFiscal || ""}
                onChange={handleBillingInputChange(index, "nameFiscal")}
                fullWidth
                variant="outlined"
                disabled={!billing.isEditing}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                  },
                }}
              />
            </Box>
            <Box
              sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
            >
              <Typography variant="body2" fontWeight="medium">
                {t("billing.nifVat", "NIF / VAT Number")}
              </Typography>
              <TextField
                value={billing.nif || ""}
                onChange={handleBillingInputChange(index, "nif")}
                fullWidth
                variant="outlined"
                disabled={!billing.isEditing}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                  },
                }}
              />
            </Box>
          </Box>

          {/* Contact Information */}
          <Box sx={{ display: "flex", gap: 4 }}>
            <Box
              sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
            >
              <Typography variant="body2" fontWeight="medium">
                {t("billing.contactName")}
              </Typography>
              <TextField
                value={billing.contactName}
                onChange={handleBillingInputChange(index, "contactName")}
                fullWidth
                variant="outlined"
                disabled={!billing.isEditing}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                  },
                }}
              />
            </Box>
            <Box
              sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
            >
              <Typography variant="body2" fontWeight="medium">
                {t("billing.email")}
              </Typography>
              <TextField
                value={billing.email}
                onChange={handleBillingInputChange(index, "email")}
                fullWidth
                variant="outlined"
                disabled={!billing.isEditing}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                  },
                }}
              />
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 4 }}>
            <Box
              sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
            >
              <Typography variant="body2" fontWeight="medium">
                {t("billing.phoneNumber")}
              </Typography>
              <TextField
                value={billing.phoneNumber}
                onChange={handleBillingInputChange(index, "phoneNumber")}
                fullWidth
                variant="outlined"
                disabled={!billing.isEditing}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                  },
                }}
              />
            </Box>
            <Box
              sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
            >
              <Typography variant="body2" fontWeight="medium">
                {t("billing.address")}
              </Typography>
              <TextField
                value={billing.address}
                onChange={handleBillingInputChange(index, "address")}
                fullWidth
                variant="outlined"
                disabled={!billing.isEditing}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                  },
                }}
              />
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 4 }}>
            <Box
              sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
            >
              <Typography variant="body2" fontWeight="medium">
                {t("billing.city")}
              </Typography>
              <TextField
                value={billing.city}
                onChange={handleBillingInputChange(index, "city")}
                fullWidth
                variant="outlined"
                disabled={!billing.isEditing}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                  },
                }}
              />
            </Box>
            <Box
              sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
            >
              <Typography variant="body2" fontWeight="medium">
                {t("billing.postalCode")}
              </Typography>
              <TextField
                value={billing.postalCode}
                onChange={handleBillingInputChange(index, "postalCode")}
                fullWidth
                variant="outlined"
                disabled={!billing.isEditing}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                  },
                }}
              />
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 4 }}>
            <Box
              sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
            >
              <Typography variant="body2" fontWeight="medium">
                {t("billing.province")}
              </Typography>
              <TextField
                value={billing.province}
                onChange={handleBillingInputChange(index, "province")}
                fullWidth
                variant="outlined"
                disabled={!billing.isEditing}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                  },
                }}
              />
            </Box>
            <Box
              sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
            >
              <Typography variant="body2" fontWeight="medium">
                {t("billing.country")}
              </Typography>
              <TextField
                value={billing.country}
                onChange={handleBillingInputChange(index, "country")}
                fullWidth
                variant="outlined"
                disabled={!billing.isEditing}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                  },
                }}
              />
            </Box>
          </Box>

          {/* Holded */}
          <Box sx={{ display: "flex", gap: 4 }}>
            <Box
              sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
            >
              <Typography variant="body2" fontWeight="medium">
                {t("billing.holdedContactId")}
              </Typography>
              <TextField
                value={billing.holdedContactId || ""}
                onChange={handleBillingInputChange(index, "holdedContactId")}
                fullWidth
                variant="outlined"
                disabled={!billing.isEditing}
                placeholder={t("billing.holdedContactIdPlaceholder")}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                  },
                }}
              />
            </Box>
            <Box
              sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
            >
              <Typography variant="body2" fontWeight="medium">
                {t("billing.legalNote")}
              </Typography>
              <TextField
                select
                value={billing.legalNote || ""}
                onChange={handleBillingInputChange(index, "legalNote")}
                fullWidth
                variant="outlined"
                disabled={!billing.isEditing}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                  },
                }}
              >
                {LEGAL_NOTE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.labelKey} value={opt.value}>
                    {t(opt.labelKey)}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>

          {billing.isEditing && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 2,
              }}
            >
              <Button
                variant="outlined"
                onClick={() => toggleEditMode(index)}
                sx={{
                  borderRadius: "50px",
                  px: 3,
                }}
              >
                {t("common.cancel")}
              </Button>
            </Box>
          )}
        </Box>
      ))}

      {/* No Billing Entries Message */}
      {billingEntries.length === 0 && !showNewBillingForm && (
        <Box
          sx={{
            p: 4,
            borderRadius: 2,
            border: `1px dashed ${theme.palette.grey[300]}`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <Typography variant="body1" color={theme.palette.text.secondary}>
            {t("billing.noBillingEntries")}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowNewBillingForm(true)}
            sx={{
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              borderRadius: "50px",
              px: 3,
              "&:hover": {
                bgcolor: theme.palette.primary.dark,
              },
            }}
          >
            {t("billing.addFirst")}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default BillingManagement;
